/* import __COLOCATED_TEMPLATE__ from './sq-donut-chart.hbs'; */
import { reads } from '@ember/object/computed';
import { scheduleOnce } from '@ember/runloop';
import { observer, computed } from '@ember/object';
import { on } from '@ember/object/evented';
import Component from '@ember/component';
import * as d3 from 'd3';
import ResizableMixin from '@dashboard/junk-drawer/mixins/resizable-mixin'; // eslint-disable-line ember/no-mixins

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend(ResizableMixin, {
  classNames: ['donut-chart'],
  // passed in
  data: null,
  width: reads('elementWidth'),
  height: reads('elementHeight'),
  groupTransform: computed('width', 'height', function () {
    return `translate(${this.width / 2}, ${this.height / 2})`;
  }),
  svg: computed('element', function () {
    if (!this.element) {
      return null;
    }
    return d3.select(this.element.querySelector('svg'));
  }),
  // eslint-disable-next-line ember/no-on-calls-in-components
  _observeAllTheThings: on('init',
  // eslint-disable-next-line ember/no-observers
  observer('data', 'width', 'height', function () {
    return scheduleOnce('afterRender', this, this.draw);
  })),
  draw() {
    const height = this.height;
    const width = this.width;
    const data = this.data;
    if (!data || !width || !height) {
      return;
    }
    if (data.reduce((sum, d) => sum + d, 0) === 0) {
      return;
    }
    const hasLabels = data.any(d => d.label);
    const svg = this.svg;
    if (!svg) {
      return;
    }
    const maxSize = Math.min(width, height) / 2;
    let outerRadius = maxSize;
    let labelRadius = maxSize;
    if (hasLabels) {
      labelRadius -= 10; // 10 is a fudge factor!
      outerRadius = labelRadius * 0.9;
    }
    const innerRadius = outerRadius * 0.64;
    const arc = d3.svg.arc().outerRadius(outerRadius).innerRadius(innerRadius);
    const labelArc = d3.svg.arc().outerRadius(labelRadius).innerRadius(labelRadius);
    const pie = d3.layout.pie().sort(null).value(d => d.count);

    // these are the data objects we'll be rendering (original data is a .data prop)
    const wedgeData = pie(data);
    for (const d of wedgeData) {
      d.labelPosition = labelArc.centroid(d);
      d.pathStart = arc.centroid(d);
      d.midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2;
    }
    const wedges = svg.select('.donut-chart--main').selectAll('g').data(wedgeData);

    // tidy up
    wedges.exit().remove();

    // make new g elements for data that's not here
    const newWedges = wedges.enter().append('g');
    newWedges.append('path').attr('class', d => d.data.className).each(function (d) {
      this._current = d;
    }); // store the initial angles

    // update existing and new wedges together
    wedges.select('path').attr('d', arc);

    // labels
    const labels = svg.select('.donut-chart--labels').selectAll('text').data(wedgeData);

    // tidy up
    labels.exit().remove();

    // make new text elements for data that's not here
    labels.enter().append('text').text(d => d.data.label);

    // update existing and new labels together
    labels.attr('transform', function (d) {
      const pos = [...d.labelPosition];
      if (d.midAngle > Math.PI) {
        pos[0] -= 5;
      } else {
        pos[0] += 5;
      }
      return `translate(${pos})`;
    }).style('text-anchor', function (d) {
      return d.midAngle > Math.PI ? 'end' : 'start';
    }).attr('dy', '0.35em');
    const lineData = wedgeData.filter(w => w.data.label);

    // lines from labels to arcs
    const lines = svg.select('.donut-chart--lines').selectAll('polyline').data(lineData);
    lines.exit().remove();
    lines.enter().append('polyline').attr('class', 'donut-chart--line');
    lines.attr('points', d => [d.pathStart, d.labelPosition]);
  }
});